/**
 * 视觉设计 Tokens — 全局统一配置入口
 *
 * 修改颜色等视觉属性时，只需改动此文件。
 * CSS 侧对应的值定义在 src/theme/tokens.css，请保持两侧数值一致。
 *
 * 使用场景：
 *   - App.vue：AntDesign ConfigProvider 读取 colorPrimary 等 token
 *   - 图表组件：读取图表色板填充 itemStyle.color
 */

// ── 品牌主色色阶（与 tokens.css 的 --token-primary-* 保持一致，基准色 #7c5cff）──
// muse 主题「极光紫 Aurora Violet」：生成式 AI / 数字人场景的科技紫。
const primaryLight: Record<number, string> = {
  50: '#f4f2ff',
  100: '#e9e5ff',
  200: '#d6ccff',
  300: '#bda6ff',
  400: '#9f80ff',
  500: '#8b6aff',
  600: '#7c5cff',
  700: '#6444e0',
  800: '#4f36ad',
  900: '#412f85',
}

// 暗色模式下主色整体提亮一个色阶，保证深色背景上的对比度
const primaryDark: Record<number, string> = {
  50: '#14102e',
  100: '#1a1440',
  200: '#221a5c',
  300: '#2c2185',
  400: '#4c33d6',
  500: '#6b4dff',
  600: '#9d80ff',
  700: '#8a6bff',
  800: '#7059e0',
  900: '#5c4ab8',
}

export const designTokens = {
  color: {
    // ── 品牌主色色阶 ──────────────────────────────────────────────────
    /** 完整主色阶（亮色模式），读取方式：designTokens.primary[500] */
    primaryScale: { light: primaryLight, dark: primaryDark },

    // ── 品牌主色 ──────────────────────────────────────────────────────
    /** 亮色模式主色 */
    primary: '#7c5cff',
    /** 暗色模式主色（偏浅，保证深色背景下的对比度） */
    primaryDark: '#9d80ff',
    /** 主色悬停 / 加深态 */
    primaryHover: '#6444e0',

    // ── 语义色 ───────────────────────────────────────────────────────
    /** 成功 / 绿色 */
    success: '#52c41a',
    /** 警告 / 橙色 */
    warning: '#faad14',
    /** 错误 / 危险 */
    error: '#ff4d4f',
    /** 强调 / 极光青（与紫色主色形成极光互补） */
    accent: '#22d3ee',

    // ── 页面背景（亮色模式）──────────────────────────────────────────
    /** 页面底层背景 */
    surface: '#ffffff',
    /** 卡片 / 容器背景 */
    panel: '#f9fafb',

    // ── 页面背景（暗色模式）──────────────────────────────────────────
    /** 页面底层背景（暗色） */
    surfaceDark: '#111827',
    /** 卡片 / 容器背景（暗色） */
    panelDark: '#1f2937',

    // ── 正文文字 ─────────────────────────────────────────────────────
    /** 正文颜色（亮色） */
    text: '#111827',
    /** 正文颜色（暗色） */
    textDark: '#f3f4f6',
  },

  /**
   * 图表专用色板 — 所有 ECharts 配置统一从这里取值
   * 约定：series[0] 主序列，series[1] 辅序列，其余按需取用
   */
  chart: {
    /** 数据序列（按顺序循环使用） */
    series: ['#7c5cff', '#22d3ee', '#38bdf8', '#14b8a6', '#f59e0b', '#f97316', '#ef4444', '#e879f9'],
    /** 主序列（请求量、PV 等） */
    primary: '#7c5cff',
    /** 辅序列（UV、Token 等） */
    secondary: '#22d3ee',
    /** 第三序列 */
    tertiary: '#38bdf8',
    /** 成功 / 正常 */
    success: '#52c41a',
    /** 警告 */
    warning: '#f59e0b',
    /** 错误 / 失败 */
    danger: '#ef4444',
    /** 崩溃 / 严重 */
    severe: '#f97316',
    /** 强调 / 品红 */
    accent: '#e879f9',
    /** 系统健康（正常） */
    healthy: '#7c5cff',
    /** 系统健康（子层级，深一档） */
    healthyDeep: '#6444e0',
    /** 热力图渐变（低 → 高） */
    heatRange: ['#e9e5ff', '#7c5cff'],
    /** 地图底图 */
    mapArea: '#f4f2ff',
    /** 地图描边 */
    mapBorder: '#c9baff',
    /** 面积填充透明度后缀（拼接在主色之后，如 `${primary}1a`） */
    areaAlpha: '1a',
    /** 柱状图圆角 */
    barRadius: [4, 4, 0, 0] as number[],
    /** 横向条形图圆角 */
    barRadiusH: [0, 4, 4, 0] as number[],
    /** 坐标轴 / 网格线（亮色） */
    axisLine: '#e5e7eb',
    splitLine: '#f3f4f6',
    /** 坐标轴 / 网格线（暗色） */
    axisLineDark: '#3f3f46',
    splitLineDark: '#27272a',
    /** 图表文字（暗色） */
    labelDark: '#a1a1aa',
  },
} as const
